import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth }           from '../context/AuthContext';
import DashboardNav          from '../components/dashboard/DashboardNav';
import SubscriptionBanner    from '../components/dashboard/SubscriptionBanner';
import ComingUpSection       from '../components/dashboard/ComingUpSection';
import PeopleSection         from '../components/dashboard/PeopleSection';
import ReferralSection       from '../components/dashboard/ReferralSection';
import SendNowModal          from '../components/dashboard/SendNowModal';
import EnvelopeAnimation     from '../components/cards/EnvelopeAnimation';
import CardReviewModal, { type ReviewOccasion } from '../components/cards/CardReviewModal';
import { type DesignData }   from '../components/cards/CardDesignPreview';
import { apiFetch }          from '../lib/api';
import type { Contact, Occasion } from '../../../shared/src/types/database';

type ContactWithOccasions = Contact & { occasions: Occasion[] };

// ─── Data hook ────────────────────────────────────────────────────────────────

function useContacts() {
  const [contacts, setContacts] = useState<ContactWithOccasions[]>([]);
  const [loading,  setLoading ] = useState(true);
  const [error,    setError   ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const json = await apiFetch<{ contacts: ContactWithOccasions[] }>('/api/contacts');
      setContacts(json.contacts ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { contacts, setContacts, loading, error, reload: load };
}

// ─── 14-day review flow (envelope animation + review modal) ──────────────────

type ReviewPhase = 'animation' | 'modal' | null;

function useCardReview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewId = searchParams.get('review');

  const [phase,     setPhase]     = useState<ReviewPhase>(reviewId ? 'animation' : null);
  const [occasion,  setOccasion]  = useState<ReviewOccasion | null>(null);
  const [design,    setDesign]    = useState<DesignData | null>(null);

  useEffect(() => {
    if (!reviewId) return;
    // Fetch the occasion + its design
    apiFetch<{ occasion: ReviewOccasion & { contacts: { name: string }; card_design_id?: string } }>(`/api/occasions/${reviewId}`)
      .then((json) => {
        const occ = json.occasion;
        const contact = (occ as { contacts?: { id?: string; name: string; relationship_type?: string } }).contacts;
        const mapped: ReviewOccasion = {
          id:                   occ.id,
          occasion_type:        occ.occasion_type,
          custom_occasion_name: occ.custom_occasion_name,
          next_send_date:       occ.next_send_date,
          current_message:      occ.current_message,
          card_design_id:       occ.card_design_id ?? null,
          contact_id:           contact?.id ?? null,
          contact_name:         contact?.name ?? 'your recipient',
          relationship_type:    contact?.relationship_type ?? null,
          card_greeting:        (occ as { card_greeting?: string | null }).card_greeting ?? null,
          card_type:            (contact as { card_type?: string } | undefined)?.card_type ?? 'folded',
        };
        setOccasion(mapped);

        // Fetch design if one is set
        if (mapped.card_design_id) {
          apiFetch<{ design: DesignData }>(`/api/designs/${encodeURIComponent(mapped.card_design_id)}`)
            .then((d) => setDesign(d.design ?? null))
            .catch(() => {});
        }
      })
      .catch(() => {
        // If we can't load the occasion, clear the review param and skip
        console.warn('[DashboardPage] Could not load review occasion, skipping');
        setPhase(null);
        setSearchParams((p) => { p.delete('review'); return p; }, { replace: true });
      });
  }, [reviewId]);

  function dismissReview() {
    setPhase(null);
    setSearchParams((p) => { p.delete('review'); return p; }, { replace: true });
  }

  return { phase, setPhase, occasion, design, dismissReview };
}

// ─── DashboardPage ────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { profile, loading: authLoading, refreshProfile } = useAuth();
  const navigate           = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contacts, setContacts, loading, error, reload } = useContacts();
  const review = useCardReview();
  const [showSendNow, setShowSendNow] = useState(false);

  // ── Refresh profile after returning from Stripe portal/checkout ──────────
  // The PricingCard sends users to Stripe with returnPath=/dashboard?subscribed=1
  // (or trial_started=1). Our local AuthContext profile is stale after that
  // round-trip — the webhook may have updated tier/status but the SPA hasn't
  // re-fetched. Without this, users see their old tier until the next full
  // page reload.
  useEffect(() => {
    if (searchParams.get('subscribed') === '1' || searchParams.get('trial_started') === '1') {
      refreshProfile();
      const next = new URLSearchParams(searchParams);
      next.delete('subscribed');
      next.delete('trial_started');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshProfile]);

  // New users who haven't added contacts yet → send to onboarding.
  // Guard: only redirect when the fetch actually succeeded (no error).
  // A failed API call leaves contacts empty but must not trap existing users.
  useEffect(() => {
    if (!loading && !authLoading && !error && contacts.length === 0) {
      const t = setTimeout(() => navigate('/onboarding', { replace: true }), 500);
      return () => clearTimeout(t);
    }
  }, [loading, contacts.length, authLoading, error, navigate]);

  // Optimistic contact add — includes any auto-created holiday occasions
  function handleContactAdded(c: Contact, autoOccasions?: Occasion[]) {
    setContacts((prev) => [{ ...c, occasions: autoOccasions ?? [] }, ...prev]);
  }

  // Optimistic contact update
  function handleContactUpdated(updated: Contact) {
    setContacts((prev) =>
      prev.map((c) =>
        c.id === updated.id ? { ...updated, occasions: c.occasions } : c,
      ),
    );
  }

  // Optimistic contact delete
  function handleContactDeleted(id: string) {
    setContacts((prev) => prev.filter((c) => c.id !== id));
  }

  // Optimistic occasion add
  function handleOccasionAdded(o: Occasion) {
    setContacts((prev) =>
      prev.map((c) =>
        c.id === o.contact_id
          ? { ...c, occasions: [...c.occasions, o] }
          : c,
      ),
    );
  }

  // Optimistic occasion update (e.g. card design selection from Coming Up)
  function handleOccasionUpdated(updated: Occasion) {
    setContacts((prev) =>
      prev.map((c) =>
        c.id === updated.contact_id
          ? { ...c, occasions: c.occasions.map((o) => o.id === updated.id ? updated : o) }
          : c,
      ),
    );
  }

  // Optimistic occasion delete
  function handleOccasionDeleted(occasionId: string) {
    setContacts((prev) =>
      prev.map((c) => ({
        ...c,
        occasions: c.occasions.filter((o) => o.id !== occasionId),
      })),
    );
  }

  const subscriptionActive =
    profile?.subscription_status === 'active' ||
    profile?.subscription_status === 'trialing';

  return (
    <div className="min-h-screen bg-cream">
      <DashboardNav />
      <SubscriptionBanner />

      {/* 14-day notification: envelope animation */}
      {review.phase === 'animation' && (
        <EnvelopeAnimation
          design={review.design}
          contactName={review.occasion?.contact_name ?? 'your recipient'}
          message={review.occasion?.current_message ?? ''}
          cardGreeting={review.occasion?.card_greeting}
          cardClosing={profile?.card_closing ?? 'With love,'}
          cardFromLine={profile?.card_from_line ?? profile?.first_name ?? ''}
          recipient={(() => {
            const c = contacts.find((c) => c.id === review.occasion?.contact_id);
            return c
              ? { name: c.name, street: c.address_street, city: c.address_city, state: c.address_state, postal: c.address_postal }
              : null;
          })()}
          onComplete={() => review.setPhase('modal')}
        />
      )}

      {/* 14-day notification: card review modal */}
      {review.phase === 'modal' && review.occasion && (
        <CardReviewModal
          occasion={review.occasion}
          design={review.design}
          onClose={review.dismissReview}
          cardClosing={profile?.card_closing ?? 'With love,'}
          cardFromLine={profile?.card_from_line ?? profile?.first_name ?? ''}
          handwritten={(review.occasion as ReviewOccasion & { handwritten?: boolean })?.handwritten ?? false}
        />
      )}

      {/* Send Now modal (Premium / tier 3 only) */}
      {showSendNow && (
        <SendNowModal
          contacts={contacts}
          allowanceBalance={profile?.card_allowance_balance ?? 0}
          onClose={() => setShowSendNow(false)}
        />
      )}

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">

        {/* ── Greeting ────────────────────────────────────────────────── */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy">
              {greeting()}, {profile?.first_name ?? 'there'} 👋
            </h1>
            {!subscriptionActive && !authLoading && (
              <p className="font-body text-sm text-gray-500 mt-1">
                Start a free trial to activate card sending.{' '}
                <Link to="/pricing" className="text-sage hover:underline font-medium">
                  See plans →
                </Link>
              </p>
            )}
          </div>
          {/* Send Now button — Premium only */}
          {profile?.tier === 3 && subscriptionActive && !loading && contacts.length > 0 && (
            <button
              type="button"
              onClick={() => setShowSendNow(true)}
              className="flex-shrink-0 flex items-center gap-2 rounded-xl bg-sage text-navy px-4 py-2.5 font-body text-sm font-semibold hover:bg-sage/90 transition-colors shadow-sm"
            >
              <span>✉️</span>
              <span>Send a card now</span>
            </button>
          )}
        </div>

        {/* ── Error ───────────────────────────────────────────────────── */}
        {error && (
          <div className="mb-6 rounded-xl bg-red-50 border border-red-100 px-4 py-3 flex items-center justify-between">
            <p className="font-body text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={reload}
              className="font-body text-xs font-semibold text-red-600 hover:text-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Loading skeleton ─────────────────────────────────────────── */}
        {loading ? (
          <div className="flex flex-col gap-8">
            <SkeletonSection label="Coming Up" count={3} />
            <SkeletonSection label="Your People" count={4} />
          </div>
        ) : (
          <>
            <ComingUpSection
              contacts={contacts}
              onOccasionUpdated={handleOccasionUpdated}
              onOccasionDeleted={handleOccasionDeleted}
            />
            <PeopleSection
              contacts={contacts}
              onContactAdded={handleContactAdded}
              onContactUpdated={handleContactUpdated}
              onContactDeleted={handleContactDeleted}
              onOccasionAdded={handleOccasionAdded}
              onOccasionUpdated={handleOccasionUpdated}
              onOccasionDeleted={handleOccasionDeleted}
            />
<ReferralSection />
            <WrappedBanner />
          </>
        )}
      </main>
    </div>
  );
}

// ─── Wrapped banner ───────────────────────────────────────────────────────────

function WrappedBanner() {
  const year = new Date().getFullYear();
  return (
    <section className="mb-10">
      <Link
        to="/wrapped"
        className="group flex items-center justify-between gap-4 rounded-[20px] overflow-hidden
                   bg-[#1A1410] px-6 py-5 shadow-warm hover:opacity-90 transition-opacity"
      >
        <div>
          <p className="font-body text-xs text-[#B59518] font-semibold uppercase tracking-wider mb-1">
            New
          </p>
          <p className="font-heading text-lg font-bold text-white">
            Your {year} Relationship Wrapped
          </p>
          <p className="font-body text-sm text-white/60 mt-0.5">
            See every card, every person, every moment, all in one place.
          </p>
        </div>
        <span className="text-4xl flex-shrink-0 group-hover:scale-110 transition-transform">🎴</span>
      </Link>
    </section>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function SkeletonSection({ label, count }: { label: string; count: number }) {
  return (
    <section>
      <div className="h-6 w-32 bg-gray-100 rounded-lg mb-4 animate-pulse" />
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="h-36 rounded-[20px] bg-white shadow-warm animate-pulse"
          />
        ))}
      </div>
    </section>
  );
}
